// The waitlist request form's model: what the drawer holds, what it checks, and
// what it sends. Pure, so every rule here is tested without a screen.
//
// ## When they can come in: parts of the day, not clock times
//
// A patient answers "which mornings, afternoons or evenings work for you" more
// easily, and more truthfully, than "from 9:30 to 11:00". The parts map onto
// the engine's bands exactly (Morning 00:00–12:00, Afternoon 12:00–17:00,
// Evening 17:00–24:00), and the clinic's own hours bound them anyway: an
// opening only exists inside them. Neighbouring parts on one day are sent as
// one band, and read back as the parts they span.
//
// A request whose bands are not made of whole parts (the front desk filed
// 9:30–11:00) is shown as it is and kept unless the patient chooses new times:
// the screen never rounds somebody's times into ones they did not give.

/** Minutes in a day: the end of the evening. */
const DAY_END = 24 * 60;
const DAY_MS = 86_400_000;

/** The parts of a day a patient picks from, with the minutes each one spans. */
export const PARTS = Object.freeze([
  {
    key: "morning",
    label: "Morning",
    hint: "Before 12 pm",
    startMinute: 0,
    endMinute: 12 * 60,
  },
  {
    key: "afternoon",
    label: "Afternoon",
    hint: "12 to 5 pm",
    startMinute: 12 * 60,
    endMinute: 17 * 60,
  },
  {
    key: "evening",
    label: "Evening",
    hint: "After 5 pm",
    startMinute: 17 * 60,
    endMinute: DAY_END,
  },
]);

/** Monday first, as a week is read; the engine numbers Sunday 0. */
export const DAYS = Object.freeze([
  {dayOfWeek: 1, label: "Monday", short: "Mon"},
  {dayOfWeek: 2, label: "Tuesday", short: "Tue"},
  {dayOfWeek: 3, label: "Wednesday", short: "Wed"},
  {dayOfWeek: 4, label: "Thursday", short: "Thu"},
  {dayOfWeek: 5, label: "Friday", short: "Fri"},
  {dayOfWeek: 6, label: "Saturday", short: "Sat"},
  {dayOfWeek: 0, label: "Sunday", short: "Sun"},
]);

/** How long to stay on the list (the server's default is 90 days). */
export const STAYS = Object.freeze([
  {days: 14, label: "2 weeks"},
  {days: 30, label: "1 month"},
  {days: 90, label: "3 months"},
  {days: 180, label: "6 months"},
]);
const DEFAULT_STAY_DAYS = 90;

/** "YYYY-MM-DD" plus n days: calendar arithmetic on a civil date, no clock and no zone. */
export function addCivilDays(day, days) {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date + days)).toISOString().slice(0, 10);
}

const DAY_WORDS = new Intl.DateTimeFormat("en-CA", {month: "long", day: "numeric", year: "numeric", timeZone: "UTC"});

/** A civil date in words: "November 3, 2027". */
export function dayInWords(day) {
  const [year, month, date] = day.split("-").map(Number);
  return DAY_WORDS.format(new Date(Date.UTC(year, month - 1, date)));
}

/**
 * The stays a site offers, each with the day it ends: the presets that end by the site's latest last day (the
 * server's lastDay.js sends the site's `today` and `latest` with `askAt`, on the site's calendar, the patient's own
 * limit). Where none fits, one stay to the latest day itself. Every preset, without a day, while the site's days are
 * unknown (a server too old to send them): the server's refusal says the rest.
 */
export function staysWithin(lastDay) {
  if (!lastDay?.today || !lastDay?.latest) return STAYS.map((stay) => ({...stay, day: null}));
  const within = STAYS.map((stay) => ({...stay, day: addCivilDays(lastDay.today, stay.days)})).filter(
    (stay) => stay.day <= lastDay.latest
  );
  if (within.length > 0) return within;
  const days = Math.round((Date.parse(lastDay.latest) - Date.parse(lastDay.today)) / DAY_MS);
  return [{days, label: `Until ${dayInWords(lastDay.latest)}`, day: lastDay.latest}];
}

/** The stay a new request starts on: three months, else the longest the site offers. */
export function defaultStayDays(stays) {
  return stays.some((stay) => stay.days === DEFAULT_STAY_DAYS) ? DEFAULT_STAY_DAYS : stays[stays.length - 1].days;
}

/** The notice a patient needs before a time they are offered. */
export const NOTICES = Object.freeze([
  {minutes: 0, label: "Any notice, even the same day"},
  {minutes: 120, label: "At least 2 hours"},
  {minutes: 1440, label: "At least a day"},
  {minutes: 2880, label: "At least 2 days"},
]);

const BOUNDARIES = new Set(
  PARTS.flatMap((part) => [part.startMinute, part.endMinute])
);
const cell = (dayOfWeek, partKey) => `${dayOfWeek}:${partKey}`;

/**
 * The parts a set of bands spans, or null when any band is not made of whole
 * parts (then the bands are kept as they are).
 */
export function partsOfBands(bands) {
  const cells = new Set();
  for (const band of bands || []) {
    if (!BOUNDARIES.has(band.startMinute) || !BOUNDARIES.has(band.endMinute))
      return null;
    PARTS.filter(
      (part) =>
        part.startMinute >= band.startMinute && part.endMinute <= band.endMinute
    ).forEach((part) => cells.add(cell(band.dayOfWeek, part.key)));
  }
  return cells;
}

/** The bands the chosen parts make: one per run of neighbouring parts on a day. */
export function bandsOfParts(cells) {
  const bands = [];
  for (const {dayOfWeek} of DAYS) {
    let run = null;
    for (const part of PARTS) {
      if (cells.has(cell(dayOfWeek, part.key))) {
        if (run) run.endMinute = part.endMinute;
        else
          run = {
            dayOfWeek,
            startMinute: part.startMinute,
            endMinute: part.endMinute,
          };
      } else if (run) {
        bands.push(run);
        run = null;
      }
    }
    if (run) bands.push(run);
  }
  return bands;
}

/** Toggles one part of one day. */
export function togglePart(cells, dayOfWeek, partKey) {
  const next = new Set(cells);
  const key = cell(dayOfWeek, partKey);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export const hasPart = (cells, dayOfWeek, partKey) =>
  cells.has(cell(dayOfWeek, partKey));

/**
 * What the drawer starts from: a new request (`entry` null) at the only site
 * that takes requests, if there is one; or the request being changed, as it is.
 */
export function draftOf(entry, {siteIds = []} = {}) {
  if (!entry) {
    return {
      locationId: siteIds.length === 1 ? siteIds[0] : "",
      serviceId: "",
      clinicianId: "",
      when: "any",
      cells: new Set(),
      keptBands: [],
      stay: {days: DEFAULT_STAY_DAYS},
      noticeMinutes: 0,
    };
  }
  const bands = (entry.bands || []).map(
    ({dayOfWeek, startMinute, endMinute}) => ({
      dayOfWeek,
      startMinute,
      endMinute,
    })
  );
  const cells = partsOfBands(bands);
  return {
    locationId: entry.locationId || "",
    serviceId: entry.serviceId ? String(entry.serviceId) : "",
    // `null` is any clinician; "" is this form's spelling of it.
    clinicianId: entry.clinicianId || "",
    when: bands.length === 0 ? "any" : cells ? "parts" : "kept",
    cells: cells || new Set(),
    keptBands: cells ? [] : bands,
    stay: Number.isFinite(entry.validUntil)
      ? {until: entry.validUntil}
      : {days: DEFAULT_STAY_DAYS},
    noticeMinutes: Number.isInteger(entry.minimumNoticeMinutes)
      ? entry.minimumNoticeMinutes
      : 0,
  };
}

/** The problems with a draft, each said next to its field and in the summary, in the form's order. */
export function problemsOf(draft) {
  const problems = [];
  if (!draft.locationId)
    problems.push({
      field: "location",
      message: "Choose where you would like to be seen.",
    });
  // The services are those of the chosen location: asked once there is one.
  if (draft.locationId && !draft.serviceId)
    problems.push({
      field: "service",
      message: "Choose what you are waiting for.",
    });
  if (draft.when === "parts" && draft.cells.size === 0) {
    problems.push({
      field: "when",
      message:
        "Choose at least one morning, afternoon or evening, or choose any time.",
    });
  }
  return problems;
}

/** What the patient's doors read: never `kind`, an anchored visit, who the patient is, or a length (the service's). */
export function bodyOf(draft, {now, lastDay = null}) {
  const bands =
    draft.when === "any"
      ? []
      : draft.when === "kept"
        ? draft.keptBands
        : bandsOfParts(draft.cells);
  return {
    locationId: draft.locationId,
    serviceId: draft.serviceId,
    clinicianId: draft.clinicianId || null,
    bands,
    minimumNoticeMinutes: draft.noticeMinutes,
    // The request's own last day as it is stored; else the day a stay ends on the site's calendar, which the server
    // places there ("YYYY-MM-DD"); else, from a server too old to send the site's days, a stay from now.
    validUntil:
      draft.stay.until ??
      (lastDay?.today ? addCivilDays(lastDay.today, draft.stay.days) : now + draft.stay.days * DAY_MS),
  };
}
